import DefaultHorizontalRule from '@tiptap/extension-horizontal-rule';
import { PluginKey, Plugin } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { TiptapNodeType } from '../tiptapTypes';
import { InputRule } from '@tiptap/core';
import { GapCursor } from '@tiptap/pm/gapcursor';

const HR_SELECTION_PLUGIN_NAME = 'addHorizontalRuleSelection';

/**
 * Extends the default horizontal rule extension from Tiptap so that it can
 * handle "***" and "___" as well.
 */
export const HorizontalRule = DefaultHorizontalRule.extend({
    addInputRules() {
        const hrType = this.type;

        return [
            new InputRule({
                find: /^(?:---|—-|___|\*\*\*)$/,
                handler: ({ state, range }) => {
                    const { tr } = state;

                    const $start = tr.doc.resolve(range.from);

                    // Only allow HR at the top level
                    if ($start.depth !== 1) return;

                    const blockRange = $start.blockRange();

                    if (!blockRange) return;

                    // Ensure the HR match covers the entire block
                    if (blockRange.start !== range.from - 1) return;

                    // If the HR is inserted prior to text, don't delete that text
                    const insertionEnd = blockRange.end === range.to + 1 ? blockRange.end : range.to;

                    const newNode = hrType.create();

                    const updatedTr = tr.replaceWith(blockRange.start, insertionEnd, newNode);

                    // This will likely occur when the HR is inserted at the end of the document
                    //  - The selection can't be a TextSelection on a non-text node, so it becomes a node selection
                    //  - Instead, we want a GapCursor selection after the HR
                    if (updatedTr.selection.from !== updatedTr.selection.to) {
                        const gapCursorSelection = new GapCursor(updatedTr.selection.$to);
                        updatedTr.setSelection(gapCursorSelection);
                    }
                },
            }),
        ];
    },

    // Adds a 'selected' class to horizontal rules when they are selected.
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey(HR_SELECTION_PLUGIN_NAME),
                props: {
                    decorations(state) {
                        const selection = state.selection;
                        const decorations: Decoration[] = [];

                        state.doc.nodesBetween(selection.from, selection.to, (node, position) => {
                            if (node.type?.name === TiptapNodeType.horizontalRule) {
                                decorations.push(
                                    Decoration.node(position, position + node.nodeSize, { class: 'selected' }),
                                );
                            }
                        });

                        return DecorationSet.create(state.doc, decorations);
                    },
                },
            }),
        ];
    },
});
